import { ref, onMounted } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { injectService } from '@patsnap/tahiti-core';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { UserInfoService } from '@telecom/shared/domain/permission';

/**
 * 用户信息组合式函数
 *
 * 提供用户相关的信息和状态，包括：
 * - 判断是否为个人租户
 * - 判断是否为企业租户
 * - 判断是否为 Pro 用户
 * - 判断是否为免费用户
 * - 用户订阅信息
 */
export const useUserInfo = createSharedComposable(() => {
    const userInfoService = injectService<UserInfoService>(UserInfoService);
    const { disposes } = useDisposes();

    const currentSubscription = ref(userInfoService.getCurrentSubscription());
    const isLoadingSubscription = ref(userInfoService.isLoadingSubscription());
    const isEnterpriseTenant = ref(userInfoService.isEnterpriseTenant());
    const isPersonalTenant = ref(userInfoService.isPersonalTenant());
    const isPro = ref(userInfoService.isPro());
    const isFreeUser = ref(userInfoService.isFreeUser());

    // 订阅服务事件，同步状态到响应式变量
    disposes.push(
        userInfoService.onSubscriptionChange(() => {
            currentSubscription.value = userInfoService.getCurrentSubscription();
            isLoadingSubscription.value = userInfoService.isLoadingSubscription();
            isEnterpriseTenant.value = userInfoService.isEnterpriseTenant();
            isPersonalTenant.value = userInfoService.isPersonalTenant();
            isPro.value = userInfoService.isPro();
            isFreeUser.value = userInfoService.isFreeUser();
        }),
    );

    /**
     * 获取用户订阅信息
     */
    async function fetchSubscriptionList() {
        await userInfoService.fetchSubscriptionList();
    }

    // 组件挂载时自动获取订阅信息
    onMounted(() => {
        fetchSubscriptionList();
    });

    return {
        isPersonalTenant,
        isEnterpriseTenant,
        isPro,
        isFreeUser,
        currentSubscription,
        isLoadingSubscription,
        fetchSubscriptionList,
    };
});
